import React, { useEffect, useRef, useState } from "react";
import { MapContainer, Marker, Popup, TileLayer, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import io from "socket.io-client";
import L from "leaflet";
import { WarningOutlined } from "@ant-design/icons";
import { withNamespaces } from "react-i18next"; // Component to update map view when driver location changes
// Fix Leaflet icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl:
    "https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png",
});

// Component to update map view when driver location changes
function MapUpdater({ driverLocation }) {
  const map = useMap();
  const [userInteracted, setUserInteracted] = useState(false);

  useEffect(() => {
    map.on("move", () => {
      setUserInteracted(true); // User has manually panned
    });

    map.on("zoom", () => {
      setUserInteracted(true); // User has manually zoomed
    });

    return () => {
      map.off("move");
      map.off("zoom");
    };
  }, [map]);

  useEffect(() => {
    if (!userInteracted && driverLocation) {
      map.setView([driverLocation.lat, driverLocation.lng], 14);
    }
  }, [driverLocation, map, userInteracted]);

  return null;
}

const DriverTrackingMap = ({
  driverId,
  destinationLocation,
  status,
  orderTracking,
  branchLocation,
  t,
}) => {
  const [driverLocation, setDriverLocation] = useState({
    lat: 0,
    lng: 0,
  });
  const [socketConnected, setSocketConnected] = useState(false);
  const [currentOrder, setCurrentOrder] = useState(null);
  const [currentDriverId, setCurrentDriverId] = useState(null);

  const [isLoading, setIsLoading] = useState(false);
  const mapRef = useRef(null);

  // Fixed destination location

  useEffect(() => {
    const socket = io("https://drivers-sockets.ordable.com/"); // no callback here

    // Listen for successful connection
    socket.on("connect", () => {
      setSocketConnected(socket.connected);
    });
    socket.on(
      "updateDriver",
      ({ driverID, latitude, longitude, currentOrder }) => {
        if (driverID === driverId && currentOrder === orderTracking) {
          setDriverLocation({ lat: latitude, lng: longitude });
        }
        setCurrentOrder(currentOrder);
        setCurrentDriverId(driverID);
      }
    );

    socket.emit("requestTracking", {
      driverId: driverId,
      orderTracking: orderTracking,
    });

    socket.on("getNewOrderLocation", ({ orderId }) => {
      if (orderId === orderTracking) {
        socket.emit("requestTracking", {
          driverId: driverId,
          orderTracking: orderTracking,
        });
      }
      setCurrentOrder(orderId);
    });

    socket.on("disconnect", () => {
      setSocketConnected(false); // Update state when disconnected
    });

    if (status === "Complete") {
      socket.emit("stopTracking", { driverId: driverId });
    }

    // Clean up on component unmount
    return () => {
      socket.off("updateDriver");
      socket.emit("stopTracking", { driverId: driverId });
      socket.disconnect();
    };
  }, [driverId, status, currentOrder, currentDriverId]);

  // Custom icons
  const driverIcon = new L.Icon({
    iconUrl:
      "https://tapcom-live.ams3.cdn.digitaloceanspaces.com/drivers/driver-icon.png",
    iconSize: [35, 35],
    iconAnchor: [17, 35],
    popupAnchor: [0, -30],
  });
  const destinationIcon = new L.Icon({
    iconUrl:
      "https://tapcom-live.ams3.cdn.digitaloceanspaces.com/drivers/CustomerPin.png",
    iconSize: [35, 35],
    iconAnchor: [17, 35],
    popupAnchor: [0, -30],
  });
  const branchIcon = new L.Icon({
    iconUrl:
      "https://tapcom-live.ams3.cdn.digitaloceanspaces.com/drivers/branchIcon.png",
    iconSize: [35, 35],
    iconAnchor: [17, 35],
    popupAnchor: [0, -30],
  });

  if (orderTracking !== currentOrder || driverId !== currentDriverId) {
    return (
      <div
        style={{
          textAlign: "center",
          fontSize: 16,
          padding: 10,
          fontWeight: "bold",
        }}
      >
        {t("Waiting for driver to start delivering...")}
      </div>
    );
  }

  return (
    socketConnected &&
    driverLocation?.lat !== 0 &&
    driverLocation?.lng !== 0 && (
      <>
        <div
          style={{
            width: "100%",
            height: "500px",
            position: "relative",
          }}
        >
          <MapContainer
            center={[driverLocation.lat, driverLocation.lng]}
            style={{ height: "100%", width: "100%", borderRadius: 5 }}
            ref={mapRef}
          >
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            />

            {/* Keep map centered on driver */}
            <MapUpdater driverLocation={driverLocation} />

            {/* Driver marker */}
            <Marker
              position={[driverLocation.lat, driverLocation.lng]}
              icon={driverIcon}
            >
              <Popup>{t("Driver")}</Popup>
            </Marker>

            {destinationLocation && (
              <Marker
                position={[destinationLocation.lat, destinationLocation.lng]}
                icon={destinationIcon}
              >
                <Popup>{t("Customer")}</Popup>
              </Marker>
            )}

            {branchLocation && (
              <Marker
                position={[branchLocation.lat, branchLocation.lng]}
                icon={branchIcon}
              >
                <Popup>{t("Branch")}</Popup>
              </Marker>
            )}
          </MapContainer>
        </div>
        <div className={"mt-4"}>
          {destinationLocation === null || destinationLocation === "" ? (
            <div className="p-2">
              <WarningOutlined style={{ color: "red", fontSize: "20px" }} />
              <span
                className={"ml-3"}
                style={{
                  fontSize: "14px",
                  color: "red",
                  fontWeight: "bold",
                }}
              >
                {t("Customer location is not available.")}
              </span>
            </div>
          ) : (
            ""
          )}

          {branchLocation === null || branchLocation === "" ? (
            <div className="p-2">
              <WarningOutlined style={{ color: "red", fontSize: "20px" }} />
              <span
                className={"ml-3"}
                style={{
                  fontSize: "14px",
                  color: "red",
                  fontWeight: "bold",
                }}
              >
                {t(
                  "Branch location is not available. Please check the branch location."
                )}
              </span>
            </div>
          ) : (
            ""
          )}
        </div>
      </>
    )
  );
};

export default withNamespaces()(DriverTrackingMap);
